import { create } from "zustand"
import { DateTime } from "luxon"
import { councilDeviceApi } from "@/lib/api"
import { queryClient } from "@/lib/queryClient"
import useGetIntervalFlow from "@/hooks/api/use-get-interval-flow"
import type { ApiResponseIntervalFlowDTO } from "@/gen/api"
import type { DataPoint } from "@/gen/api"
import type { IntervalFlowDTO } from "@/gen/api"

export enum ChartViewModeEnum {
  Hourly = "hourly",
  Daily = "daily",
  Weekly = "weekly",
}

export enum DetailTab {
  WaterConsumption = "WaterConsumption",
  RecentAlarms = "RecentAlarms",
}

type Measurement = {
  index: number
  type: string
  time: DateTime
  unit: string
}

export enum FetchType {
  initial,
  interval,
  final,
}

// Helper functions for computed values
export const computeIntervalFlowInMonth = (
  selectedMonth: DateTime,
  flowMeasurementData: Map<string, Measurement[]>,
) => {
  const key = selectedMonth.toFormat("yyyy-MM")
  const data = flowMeasurementData.get(key)
  if (data == null) return []
  const uniqueData = new Map<number, Measurement>()
  for (const item of data) {
    const existing = uniqueData.get(item.time.toMillis())
    if (!existing || item.index > existing.index) {
      uniqueData.set(item.time.toMillis(), item)
    }
  }
  return Array.from(uniqueData.values())
    .map((item) => ({
      index: item.index,
      time: item.time,
      type: item.type,
      unit: item.unit,
    }))
    .sort((a, b) => a.time.diff(b.time, "milliseconds").milliseconds)
}

export const computeIntervalFlowInDay = (
  selectedDate: DateTime,
  flowMeasurementData: Map<string, Measurement[]>,
) => {
  const monthKey = selectedDate.toFormat("yyyy-MM")
  const monthData = flowMeasurementData.get(monthKey)
  if (monthData == null) return []
  const uniqueData = new Map<number, Measurement>()
  const ref = selectedDate.setZone()
  const startTime = ref.startOf("day").minus({ minutes: 30 })
  const endTime = ref.plus({ day: 1 }).startOf("day")

  for (const item of monthData.filter((i) => i.time >= startTime && i.time <= endTime)) {
    const existing = uniqueData.get(item.time.toMillis())
    if (!existing || item.index > existing.index) {
      uniqueData.set(item.time.toMillis(), item)
    }
  }

  return Array.from(uniqueData.values())
    .map((item) => ({
      index: item.index,
      time: item.time,
      type: item.type,
      unit: item.unit,
    }))
    .sort((a, b) => a.time.diff(b.time, "milliseconds").milliseconds)
}

export const computeIntervalFlowInWeek = (
  selectedWeek: DateTime,
  flowMeasurementData: Map<string, Measurement[]>,
) => {
  const now = DateTime.now()
  const startOfWeek = selectedWeek.startOf("week")
  const endOfWeek = selectedWeek.endOf("week")
  const shouldUseStartOfWeek = !now.hasSame(endOfWeek, "month")
  const key = (shouldUseStartOfWeek ? startOfWeek : endOfWeek).toFormat("yyyy-MM")
  const data = flowMeasurementData.get(key)
  if (data == null) return []
  const uniqueData = new Map<number, Measurement>()
  for (const item of data) {
    const existing = uniqueData.get(item.time.toMillis())
    if (!existing || item.index > existing.index) {
      uniqueData.set(item.time.toMillis(), item)
    }
  }
  return Array.from(uniqueData.values())
    .map((item) => ({
      index: item.index,
      time: item.time,
      type: item.type,
      unit: item.unit,
    }))
    .sort((a, b) => a.time.diff(b.time, "milliseconds").milliseconds)
}

export const computeUnit = (
  selectedMonth: DateTime,
  flowMeasurementData: Map<string, Measurement[]>,
) => {
  const key = selectedMonth.toFormat("yyyy-MM")
  const data = flowMeasurementData.get(key)
  if (data == null || data.length === 0) return null
  return data[0]!.unit
}

interface IntervalFlowState {
  councilId: string | null
  deviceId: number | null
  currentTime: DateTime
  selectedDate: DateTime
  selectedMonth: DateTime
  selectedWeek: DateTime
  chartViewMode: ChartViewModeEnum
  fetchType: FetchType
  currentTab: DetailTab
  flowMeasurementData: Map<string, Measurement[]>

  // Actions
  setTab: (tab: DetailTab) => void
  setSelectedDate: (date: Date) => Promise<void>
  setSelectedMonth: (date: Date) => Promise<void>
  setSelectedWeek: (date: Date) => Promise<void>
  setChartViewMode: (mode: ChartViewModeEnum) => void
  setCouncilId: (id: string | null) => void
  setDeviceId: (id: number | null) => void
  loadIntervalFlowMeasurement: () => Promise<void>
  reset: () => void
}

export const createIntervalFlowStore = () => {
  return create<IntervalFlowState>((set, get) => {
    // Helper functions
    const getCacheKeyAndQuit = (
      chartViewMode: ChartViewModeEnum,
      selectedDate: DateTime,
      selectedMonth: DateTime,
      selectedWeek: DateTime,
      flowMeasurementData: Map<string, Measurement[]>,
    ) => {
      let key: string
      switch (chartViewMode) {
        case ChartViewModeEnum.Hourly:
          key = selectedDate.toFormat("yyyy-MM")
          break
        case ChartViewModeEnum.Daily:
          key = selectedMonth.toFormat("yyyy-MM")
          break
        case ChartViewModeEnum.Weekly:
          key = selectedWeek.toFormat("yyyy-MM")
          break
      }
      return { key, quit: flowMeasurementData.has(key) }
    }

    const getDateRange = (
      chartViewMode: ChartViewModeEnum,
      selectedDate: DateTime,
      selectedMonth: DateTime,
      selectedWeek: DateTime,
    ) => {
      const now = DateTime.now()
      let refEndDate: DateTime

      switch (chartViewMode) {
        case ChartViewModeEnum.Hourly:
          refEndDate =
            now.day === selectedDate.day &&
            now.month === selectedDate.month &&
            now.year === selectedDate.year
              ? now
              : selectedDate.endOf("month").endOf("week")
          break
        case ChartViewModeEnum.Daily:
          refEndDate =
            now.month === selectedMonth.month && now.year === selectedMonth.year
              ? now
              : selectedMonth.endOf("month").endOf("week")
          break
        case ChartViewModeEnum.Weekly:
          refEndDate =
            now.weekNumber === selectedWeek.weekNumber && now.year === selectedWeek.year
              ? now
              : selectedWeek.endOf("month").endOf("week")
          break
      }

      const toAuTime = refEndDate.setZone().plus({ day: 1 }).startOf("day").plus({ millisecond: 1 })

      let refStartDate: DateTime
      switch (chartViewMode) {
        case ChartViewModeEnum.Hourly:
          refStartDate = selectedDate
          break
        case ChartViewModeEnum.Daily:
          refStartDate = selectedMonth
          break
        case ChartViewModeEnum.Weekly:
          refStartDate = selectedWeek
          break
      }

      const fromAuTime = refStartDate
        .startOf("month")
        .startOf("week")
        .startOf("day")
        .minus({ hour: 1 })
      return { fromAuTime, toAuTime }
    }

    const fetchAllMeasurements = async (
      councilId: string,
      deviceId: number,
      fromAuTime: DateTime,
      toAuTime: DateTime,
      key: string,
    ) => {
      let pageNumber = 1
      let run = true

      do {
        const from = fromAuTime.toISO()
        const to = toAuTime.toISO()

        if (!from || !to) {
          run = false
          return null
        }

        const params = {
          from,
          to,
          deviceId,
          pageSize: 100,
          currentPage: pageNumber,
        }

        const pageData = await queryClient.fetchQuery({
          queryKey: [useGetIntervalFlow.QUERY_KEY, councilId, deviceId, params],
          queryFn: async () => {
            const response = await councilDeviceApi.getIntervalFlow(
              councilId,
              deviceId.toString(),
              from,
              to,
              pageNumber,
              100,
            )
            return (response.data as ApiResponseIntervalFlowDTO).data as IntervalFlowDTO | undefined
          },
        })

        if (get().deviceId !== deviceId || get().councilId !== councilId) {
          return
        }

        const pageDataAsMap = pageData as IntervalFlowDTO | undefined
        const newMeasurements =
          (pageDataAsMap?.data as Array<DataPoint> | undefined)?.map<Measurement>((item) => ({
            index: item.flowRate ?? item.index ?? 0,
            time: DateTime.fromISO(item.timestamp ?? ""),
            type: "",
            unit: item.unit ?? "",
          })) || []

        set((state) => {
          const updatedData = new Map(state.flowMeasurementData)
          const existingData = updatedData.get(key) || []
          updatedData.set(key, [...existingData, ...newMeasurements])
          return { flowMeasurementData: updatedData }
        })

        if (pageNumber === 1) {
          set({ fetchType: FetchType.interval })
        }

        run = pageNumber++ < (pageDataAsMap?.pagination?.totalPages ?? 0)
      } while (run)
    }

    return {
      // Initial state
      councilId: null,
      deviceId: null,
      currentTime: DateTime.now().startOf("day"),
      selectedDate: DateTime.now().startOf("day").minus({ days: 1 }),
      selectedMonth: DateTime.now().startOf("day"),
      selectedWeek: DateTime.now().startOf("day").minus({ weeks: 1 }),
      chartViewMode: ChartViewModeEnum.Hourly,
      fetchType: FetchType.final,
      currentTab: DetailTab.WaterConsumption,
      flowMeasurementData: new Map(),
      setTab: (tab: DetailTab) => {
        set({ currentTab: tab })
      },

      setSelectedDate: async (date: Date) => {
        const selectedDate = DateTime.fromJSDate(date)
        set({ selectedDate })
        await get().loadIntervalFlowMeasurement()
      },

      setSelectedMonth: async (date: Date) => {
        const selectedMonth = DateTime.fromJSDate(date)
        set({ selectedMonth })
        await get().loadIntervalFlowMeasurement()
      },

      setSelectedWeek: async (date: Date) => {
        const selectedWeek = DateTime.fromJSDate(date)
        set({ selectedWeek })
        await get().loadIntervalFlowMeasurement()
      },

      setChartViewMode: (mode: ChartViewModeEnum) => {
        set({ chartViewMode: mode })
      },

      setDeviceId: (id: number | null) => {
        if (get().deviceId !== id) {
          set({
            deviceId: id,
            flowMeasurementData: new Map(),
            fetchType: FetchType.final,
          })
        }
      },

      setCouncilId: (id: string | null) => {
        if (get().councilId !== id) {
          set({
            councilId: id,
            flowMeasurementData: new Map(),
            fetchType: FetchType.final,
          })
        }
      },

      reset: () => {
        set({
          councilId: null,
          deviceId: null,
          flowMeasurementData: new Map(),
          fetchType: FetchType.final,
        })
      },

      loadIntervalFlowMeasurement: async () => {
        const {
          chartViewMode,
          selectedDate,
          selectedMonth,
          selectedWeek,
          flowMeasurementData,
          deviceId,
          councilId,
        } = get()

        const { key, quit } = getCacheKeyAndQuit(
          chartViewMode,
          selectedDate,
          selectedMonth,
          selectedWeek,
          flowMeasurementData,
        )

        if (quit) {
          set({ fetchType: FetchType.final })
          return
        }

        if (!deviceId || !councilId) return

        if (get().fetchType !== FetchType.final) return

        set({ fetchType: FetchType.initial })

        const { fromAuTime, toAuTime } = getDateRange(
          chartViewMode,
          selectedDate,
          selectedMonth,
          selectedWeek,
        )

        await fetchAllMeasurements(councilId, deviceId, fromAuTime, toAuTime, key)

        set({ fetchType: FetchType.final })
      },
    }
  })
}
